import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { tzAbbreviation, zipInstant } from '../lib/timezone.js';
import { forceRepaintAfterOverlayClose } from '../lib/forceRepaint.js';
import { ZipOfficialAlerts } from './OfficialAlerts.jsx';
import { useUnits } from '../lib/preferences.js';

const SEV_LABEL = ['Baseline', 'Advisory', 'Action required', 'Critical'];

// Dry-bulb reference tiers for the jurisdictions whose heat rule triggers on
// air temperature rather than heat index (OR/MD/federal use heat index, and
// NV has no fixed numeric tier — none of those belong on this dry-bulb
// chart). This is presentation-only, not a re-implementation of the rule
// engine, which already decided what fired server-side.
const DRY_BULB_HEAT_TIERS = {
  CA: [
    { value: 80, label: 'shade' },
    { value: 95, label: 'high-heat' },
  ],
  WA: [
    { value: 80, label: 'baseline' },
    { value: 90, label: '10min/2hr rest' },
    { value: 100, label: '15min/hr rest' },
  ],
};

function Metric({ k, v, u, digits = 1 }) {
  return (
    <div className="metric">
      <div className="k">{k}</div>
      <div className="v">
        {v == null ? '—' : typeof v === 'number' ? v.toFixed(digits) : v}
        {v != null && u ? <span className="u">{u}</span> : null}
      </div>
    </div>
  );
}

// Shared by the "now" panel and each forecast day's popup — `d` just needs
// to carry the same field names either way (see the `metricsData` adapter
// in ForecastDayModal, which maps a forecast day's fields onto this shape).
function ConditionsMetrics({
  d,
  highLabel = 'Today high',
  lowLabel = 'Today low',
  rainLabel = 'Rain next 24h',
  rain48Label = 'Rain next 48h',
}) {
  const u = useUnits();
  const t = (f) => u.temp(f);
  const T = u.tempLabel;
  // Wet bulb is shown in both scales (research and some standards quote
  // it in °C): the preferred unit first, the other second.
  const wbC = d.wetBulbC ?? (d.wetBulbF != null ? ((d.wetBulbF - 32) * 5) / 9 : null);
  const [wb1, wb2] = u.units.temperature === 'C' ? [[wbC, '°C'], [d.wetBulbF, '°F']] : [[d.wetBulbF, '°F'], [wbC, '°C']];
  return (
    <div className="metrics">
      <Metric k="Wet bulb" v={wb1[0]} u={wb1[1]} digits={1} />
      <Metric k="Wet bulb" v={wb2[0]} u={wb2[1]} digits={1} />
      <Metric k="Dry bulb" v={t(d.tempF)} u={T} digits={1} />
      <Metric k="Dew point" v={t(d.dewPointF)} u={T} digits={1} />
      <Metric k="Humidity" v={d.humidity} u="%" digits={0} />
      <Metric k="WBGT (sun)" v={t(d.wbgtSunF)} u={T} digits={1} />
      <Metric k="WBGT (shade)" v={t(d.wbgtShadeF)} u={T} digits={1} />
      <Metric k="Heat index" v={t(d.heatIndexF)} u={T} digits={0} />
      <Metric k="Feels like" v={t(d.apparentF)} u={T} digits={0} />
      <Metric k="Wind" v={u.speed(d.windMph)} u={u.speedLabel} digits={0} />
      <Metric k="Gusts" v={u.speed(d.gustMph)} u={u.speedLabel} digits={0} />
      <Metric k="AQI PM2.5" v={d.aqiPm25} digits={0} />
      <Metric k="PM2.5" v={d.pm25} u="µg/m³" digits={1} />
      <Metric k="UV index" v={d.uvIndex} digits={0} />
      <Metric k="Solar" v={d.solarWm2} u="W/m²" digits={0} />
      <Metric k={rainLabel} v={u.precip(d.precip24In)} u={u.precipLabel} digits={u.precipDigits(2)} />
      <Metric k={rain48Label} v={u.precip(d.precip48In)} u={u.precipLabel} digits={u.precipDigits(2)} />
      <Metric k={highLabel} v={t(d.todayHighF)} u={T} digits={0} />
      <Metric k={lowLabel} v={t(d.todayLowF)} u={T} digits={0} />
      <Metric k="Prior 5d avg high" v={t(d.avgPrior5HighF)} u={T} digits={0} />
      <Metric k="Prior 5d avg low" v={t(d.avgPrior5LowF)} u={T} digits={0} />
      <Metric k="Sky" v={d.weatherText} />
      <Metric k="Status" v={SEV_LABEL[d.severity]} />
    </div>
  );
}

function WetBulbStrainBox({ wetBulbBand, wetBulbInRange, acgihAcclimatized, acgihUnacclimatized, heatAgency }) {
  return (
    <div className="rule sev-0" style={{ borderLeftColor: 'var(--accent)', background: 'var(--bg-raised)' }}>
      <div className="rule-head">
        <span className="rule-title">{wetBulbBand?.label}</span>
        <span className="rule-cite">Stull (2011){wetBulbInRange ? '' : ' — extrapolated'}</span>
      </div>
      <div className="rule-detail">{wetBulbBand?.note}</div>
      <div className="rule-detail" style={{ marginTop: 6 }}>
        ACGIH screening ceiling at this estimated WBGT: <strong>{acgihAcclimatized}</strong> workload
        for acclimatized crews, <strong>{acgihUnacclimatized}</strong> for unacclimatized. ACGIH is
        guidance, not a binding standard — the triggers below (from{' '}
        {heatAgency ?? 'the applicable jurisdiction'}) are what is enforceable.
      </div>
    </div>
  );
}

function TrendChart({ trend: rawTrend, dryBulbTiers, heatAgency, formatHour, heading }) {
  const u = useUnits();
  // Same series, converted for display (keys unchanged).
  const trend = rawTrend?.map((p) => ({ ...p, wetBulbF: u.temp(p.wetBulbF), tempF: u.temp(p.tempF), gustMph: u.speed(p.gustMph) }));
  const nowPoint = trend?.find((p) => p.isNow);
  return (
    <>
      <h3 style={{ marginTop: 18 }}>{heading}</h3>
      <div className="chart-box">
        <div className="legend">
          <span className="l-wb">Wet bulb {u.tempLabel}</span>
          <span className="l-t">Air temp {u.tempLabel}</span>
          <span className="l-g">Gusts {u.speedLabel}</span>
        </div>
        <ResponsiveContainer width="100%" height={190}>
          <LineChart data={trend} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="#2a323d" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              tickFormatter={formatHour}
              stroke="#6e7d8c"
              fontSize={10}
              interval={3}
              tickLine={false}
            />
            <YAxis stroke="#6e7d8c" fontSize={10} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                background: '#161b22',
                border: '1px solid #3a4552',
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={formatHour}
              formatter={(v, n) => [v == null ? '—' : v.toFixed(1), n]}
            />
            {nowPoint && <ReferenceLine x={nowPoint.time} stroke="#58a6ff" strokeDasharray="3 3" />}
            {dryBulbTiers?.map((t) => (
              <ReferenceLine
                key={t.value}
                y={u.temp(t.value)}
                stroke={t.value >= 90 ? '#f85149' : '#d29922'}
                strokeDasharray="4 4"
                opacity={0.5}
              />
            ))}
            <Line type="monotone" dataKey="wetBulbF" name={`Wet bulb ${u.tempLabel}`} stroke="#f0883e" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="tempF" name={`Air temp ${u.tempLabel}`} stroke="#58a6ff" strokeWidth={1.5} dot={false} />
            <Line
              type="monotone"
              dataKey="gustMph"
              name={`Gusts ${u.speedLabel}`}
              stroke="#8b949e"
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="rule-detail" style={{ marginTop: 6 }}>
        {dryBulbTiers
          ? `Dashed horizontal lines mark ${heatAgency}'s dry-bulb thresholds (` +
            dryBulbTiers.map((t) => `${u.fmtTemp(t.value)} ${t.label}`).join(', ') +
            ').'
          : `${heatAgency ?? 'This jurisdiction'}'s heat rule doesn't use a fixed dry-bulb ` +
            'threshold, so no reference lines are shown here.'}
        {nowPoint ? ' The blue vertical line is the current hour.' : ''}
      </div>
    </>
  );
}

// Single series (hourly rain), so no legend box — the heading already names
// it. A bar chart reads better than a line here since each hour's rainfall
// is a discrete amount, not a continuously varying quantity.
function RainChart({ trend: rawTrend, formatHour, heading }) {
  const u = useUnits();
  const trend = rawTrend?.map((p) => ({ ...p, precipIn: u.precip(p.precipIn) }));
  const digits = u.precipDigits(2);
  return (
    <>
      <h3 style={{ marginTop: 18 }}>{heading}</h3>
      <div className="chart-box">
        <ResponsiveContainer width="100%" height={130}>
          <BarChart data={trend} margin={{ top: 4, right: 12, left: -18, bottom: 0 }} barCategoryGap="20%">
            <CartesianGrid stroke="#2a323d" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              tickFormatter={formatHour}
              stroke="#6e7d8c"
              fontSize={10}
              interval={3}
              tickLine={false}
            />
            <YAxis
              stroke="#6e7d8c"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tickCount={4}
              tickFormatter={(v) => v.toFixed(digits)}
            />
            <Tooltip
              contentStyle={{
                background: '#161b22',
                border: '1px solid #3a4552',
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={formatHour}
              formatter={(v) => [v == null ? '—' : `${v.toFixed(digits)} ${u.precipLabel}`, 'Rain']}
            />
            <Bar dataKey="precipIn" name={`Rain (${u.precipLabel}/hr)`} fill="#39c5cf" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}

// Single series (UV index), so no legend box — the heading already names
// it. A line chart fits here since UV varies continuously through the day,
// unlike rain's discrete hourly amounts. Reference lines mark the same
// WHO/EPA thresholds (8 = very high, 11 = extreme) universal.js already
// fires rules on, so a line crossing a dashed line here is the same event
// as a UV rule appearing in Regulatory status.
function UvChart({ trend, formatHour, heading }) {
  return (
    <>
      <h3 style={{ marginTop: 18 }}>{heading}</h3>
      <div className="chart-box">
        <ResponsiveContainer width="100%" height={130}>
          <LineChart data={trend} margin={{ top: 4, right: 12, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="#2a323d" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="time"
              tickFormatter={formatHour}
              stroke="#6e7d8c"
              fontSize={10}
              interval={3}
              tickLine={false}
            />
            <YAxis stroke="#6e7d8c" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: '#161b22',
                border: '1px solid #3a4552',
                borderRadius: 6,
                fontSize: 12,
              }}
              labelFormatter={formatHour}
              formatter={(v) => [v == null ? '—' : v.toFixed(0), 'UV index']}
            />
            <ReferenceLine y={8} stroke="#d29922" strokeDasharray="4 4" opacity={0.5} />
            <ReferenceLine y={11} stroke="#f85149" strokeDasharray="4 4" opacity={0.5} />
            <Line type="monotone" dataKey="uvIndex" name="UV index" stroke="#a371f7" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="rule-detail" style={{ marginTop: 6 }}>
        Dashed lines mark the WHO/EPA scale's very-high (8) and extreme (11) thresholds.
      </div>
    </>
  );
}

const BASIS_LABEL = { forecast: 'Forecast', observed: 'Observed' };
const BASIS_TITLE = {
  forecast: 'Based on forecast rainfall: use it for preparation and planning.',
  observed: 'Based on observed rainfall or discharge: may start a permit inspection clock.',
};

// Stormwater rules carry their own context tags so a forecast-based planning
// signal can't be mistaken for an observed permit trigger, and states whose
// permit has no fixed rainfall depth say so up front, not only in the detail.
function RuleTags({ r }) {
  if (r.category !== 'stormwater') return null;
  const planningOnly = r.triggerKind === 'discharge' || r.triggerKind === 'unresearched';
  return (
    <div className="rule-tags">
      <span className="tag">Stormwater permit / inspection planning</span>
      {r.basis && (
        <span className={`tag tag-${r.basis}`} title={BASIS_TITLE[r.basis]}>
          {BASIS_LABEL[r.basis] ?? r.basis}
        </span>
      )}
      {r.accumulationHours && <span className="tag">{r.accumulationHours} h rainfall</span>}
      {planningOnly && (
        <span className="tag tag-planning">Planning signal — not a precise trigger</span>
      )}
    </div>
  );
}

// Canadian register rows carry a type: only "threshold" rows are evaluated
// triggers; the rest are law for reference, rows waiting on site input,
// rows not yet in force, or municipal operating hours (not OHS law).
const RULE_TYPE = {
  threshold: null,
  reference: { label: 'Applicable law — reference', title: 'Qualitative obligation; not a numeric trigger' },
  'needs-input': { label: 'Needs site input', title: 'Set the site’s work context to evaluate this' },
  'no-data': { label: 'No data', title: 'The weather value this rule needs is unavailable' },
  dormant: { label: 'Not yet in force', title: 'Enacted, with a future effective date' },
  operational: { label: 'Municipal bylaw — not OHS', title: 'Operating-hours bylaw; never raises a safety severity' },
};

function RuleList({ rules, heading }) {
  return (
    <>
      <h3 style={{ marginTop: 18 }}>{heading ?? `Regulatory status (${rules.length})`}</h3>
      {rules.map((r) => {
        const type = RULE_TYPE[r.ruleType];
        return (
          <div key={r.id} className={`rule sev-${r.severity ?? 'na'}`}>
            <div className="rule-head">
              <span className="rule-title">{r.title}</span>
              <span className="rule-cite">
                {r.agency ? `${r.agency} — ` : ''}
                {r.citation}
              </span>
            </div>
            <RuleTags r={r} />
            {(type || r.estimated || r.sourceUrl) && (
              <div className="rule-tags">
                {type && (
                  <span className="tag tag-type" title={type.title}>
                    {type.label}
                  </span>
                )}
                {r.estimated && (
                  <span className="tag" title="Compared against a value estimated from modeled weather">
                    Estimated value
                  </span>
                )}
                {r.id?.startsWith('CA-') && <span className="tag">{r.id}</span>}
                {r.sourceUrl && (
                  <a className="tag" href={r.sourceUrl} target="_blank" rel="noreferrer">
                    Source
                  </a>
                )}
              </div>
            )}
            <div className="rule-detail">{r.detail}</div>
          </div>
        );
      })}
      {rules.length === 0 && (
        <div className="rule sev-0">
          <div className="rule-title">No triggers</div>
          <div className="rule-detail">
            Conditions are below every monitored threshold, or there wasn't enough data to
            evaluate them.
          </div>
        </div>
      )}
    </>
  );
}

/** Which Canadian OHS regime governs this point, and why. */
function OhsRegime({ regime, note, conditional }) {
  return (
    <div className="rule sev-na ohs-regime">
      <div className="rule-head">
        <span className="rule-title">Governing OHS law: {regime.name}</span>
        <a className="rule-cite" href={regime.sourceUrl} target="_blank" rel="noreferrer">
          {regime.law}
        </a>
      </div>
      <div className="rule-detail">{regime.note}</div>
      {regime.sectorNote && <div className="rule-detail">{regime.sectorNote}</div>}
      {note && <div className="rule-detail">{note}</div>}
      {conditional?.length > 0 && (
        <div className="rule-detail">
          Also applies if the site involves{' '}
          {[...new Set(conditional.map((c) => c.label))].join(' or ')} ({conditional.map((c) => c.id).join(', ')}) — set
          it on the site to evaluate.
        </div>
      )}
      <div className="rule-detail muted">{regime.verification}</div>
    </div>
  );
}

const hourLabel = (timeZone, utcOffsetSeconds) => (iso) =>
  zipInstant(iso, utcOffsetSeconds).toLocaleTimeString('en-US', {
    hour: 'numeric',
    timeZone: timeZone || undefined,
  });

// `date` is a bare "YYYY-MM-DD" calendar date already resolved to the ZIP's
// local day server-side (Open-Meteo's timezone=auto) — parse and format it
// as UTC throughout so the browser's own timezone never shifts it by a day.
function forecastDateLabel(date, opts) {
  if (!date) return '—';
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

// Full-detail popup for one forecast day — same metrics grid, wet bulb
// strain box, trend chart, and complete rule list as the "now" panel, just
// fed from that day's own data instead of current conditions. Portaled to
// <body> and rendered as a centered, dismissible dialog (backdrop click,
// close button, or Escape) rather than anchored to a trigger element like
// the "+N more" popup, since there's too much content to fit in a small
// anchored popup.
function ForecastDayModal({ zip, day, isTomorrow, dryBulbTiers, onClose }) {
  const formatHour = hourLabel(zip.timezone, zip.utcOffsetSeconds);
  const heatAgency = zip.jurisdiction?.heat?.agency;
  const metricsData = { ...day, todayHighF: day.highF, todayLowF: day.lowF };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // See forceRepaint.js — clears the mobile Safari "stuck shifted left" bug
  // that CSS-only fixes couldn't touch. Runs on unmount only (empty deps).
  useEffect(() => () => forceRepaintAfterOverlayClose(), []);

  const modal = (
    <div
      className="forecast-detail-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="forecast-detail-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="forecast-detail-head">
          <span>
            {zip.kind === 'municipality' ? zip.place : `${zip.zip} · ${zip.place}`} —{' '}
            {isTomorrow ? 'Tomorrow' : forecastDateLabel(day.date, { weekday: 'long' })},{' '}
            {forecastDateLabel(day.date, { month: 'short', day: 'numeric' })}
          </span>
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="forecast-detail-body detail-grid">
          <div>
            <h3>Conditions</h3>
            <ConditionsMetrics
              d={metricsData}
              highLabel="Day high"
              lowLabel="Day low"
              rainLabel="Rain (calendar day)"
              rain48Label="Rain (this day + next)"
            />

            <h3>Wet bulb strain</h3>
            <WetBulbStrainBox
              wetBulbBand={day.wetBulbBand}
              wetBulbInRange={day.wetBulbInRange}
              acgihAcclimatized={day.acgihCeilingAcclimatized}
              acgihUnacclimatized={day.acgihCeilingUnacclimatized}
              heatAgency={heatAgency}
            />

            <TrendChart
              trend={day.trend}
              dryBulbTiers={dryBulbTiers}
              heatAgency={heatAgency}
              formatHour={formatHour}
              heading="Hourly forecast"
            />

            <RainChart trend={day.trend} formatHour={formatHour} heading="Rain (hourly forecast)" />
            <UvChart trend={day.trend} formatHour={formatHour} heading="UV index (hourly forecast)" />
          </div>

          <div>
            <RuleList rules={day.rules} />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// Deliberately minimal — day name, date, temperature, status, and a link to
// the full-detail popup (ForecastDayModal) for everything else, rather than
// previewing rule titles inline. With 8 cards in a row there isn't room for
// more than that without the row turning into a wall of text.
function ForecastDay({ day, isTomorrow, zip, dryBulbTiers }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const u = useUnits();
  const deg = (f) => (f != null ? Math.round(u.temp(f)) : '—');

  return (
    <div className={`rule forecast-day sev-${day.severity}`}>
      <div className="forecast-day-label">
        {isTomorrow ? 'Tomorrow' : forecastDateLabel(day.date, { weekday: 'short' })}
      </div>
      <div className="forecast-day-date">{forecastDateLabel(day.date, { month: 'short', day: 'numeric' })}</div>
      <div className="forecast-day-temp">
        {deg(day.highF)}°{' '}
        <span className="lo">/ {deg(day.lowF)}°</span>
      </div>
      <div className="forecast-day-status">{SEV_LABEL[day.severity]}</div>
      <button
        type="button"
        className="forecast-more-btn forecast-detail-trigger"
        onClick={() => setDetailOpen(true)}
      >
        Full details →
      </button>
      {detailOpen && (
        <ForecastDayModal
          zip={zip}
          day={day}
          isTomorrow={isTomorrow}
          dryBulbTiers={dryBulbTiers}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

export default function ZipDetail({ zip, onClose }) {
  // See forceRepaint.js — clears the mobile Safari "stuck shifted left" bug
  // that CSS-only fixes couldn't touch. Runs on unmount only (empty deps).
  useEffect(() => () => forceRepaintAfterOverlayClose(), []);

  const formatHour = hourLabel(zip.timezone, zip.utcOffsetSeconds);
  const observedInstant = zipInstant(zip.observedAt, zip.utcOffsetSeconds);
  const tz = tzAbbreviation(observedInstant, zip.timezone);
  const heatAgency = zip.jurisdiction?.heat?.agency;
  const dryBulbTiers = zip.jurisdiction?.heat?.tier === 'state' ? DRY_BULB_HEAT_TIERS[zip.state] : null;

  return (
    <div className="detail-inner">
      {/* Only visible below 900px (see .detail-close) — on mobile the panel
          becomes a full-screen overlay, so the row itself is no longer
          reachable to tap-to-close the way it is on desktop. */}
      <div className="detail-close">
        <span>{zip.kind === 'municipality' ? zip.place : `${zip.zip} · ${zip.place}`}</span>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="detail-grid">
        <div>
          <h3>Conditions — {zip.place}</h3>
          <ConditionsMetrics d={zip} />

          <h3>Wet bulb strain</h3>
          <WetBulbStrainBox
            wetBulbBand={zip.wetBulbBand}
            wetBulbInRange={zip.wetBulbInRange}
            acgihAcclimatized={zip.acgihCeilingAcclimatized}
            acgihUnacclimatized={zip.acgihCeilingUnacclimatized}
            heatAgency={heatAgency}
          />

          <TrendChart
            trend={zip.trend}
            dryBulbTiers={dryBulbTiers}
            heatAgency={heatAgency}
            formatHour={formatHour}
            heading="Next 24 hours"
          />

          <RainChart trend={zip.trend} formatHour={formatHour} heading="Rain (next 24 hours)" />
          <UvChart trend={zip.trend} formatHour={formatHour} heading="UV index (next 24 hours)" />
        </div>

        <div>
          <ZipOfficialAlerts zip={zip} />
          {zip.rulesEvaluated === false ? (
            <>
              <h3>Calculated triggers</h3>
              <div className="rule-detail">{zip.rulesNote ?? 'Work rules are not evaluated for this location.'}</div>
            </>
          ) : (
            <>
              {zip.ohsRegime && <OhsRegime regime={zip.ohsRegime} note={zip.rulesNote} conditional={zip.conditionalRules} />}
              <RuleList rules={zip.rules} heading={`Calculated triggers — this app (${zip.rules.length})`} />
            </>
          )}

          <h3 style={{ marginTop: 18 }}>Location</h3>
          <div className="rule-detail" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
            {zip.lat.toFixed(4)}, {zip.lon.toFixed(4)} · model time{' '}
            {observedInstant?.toLocaleString('en-US', {
              timeZone: zip.timezone || undefined,
              dateStyle: 'short',
              timeStyle: 'short',
            })}
            {tz ? ` ${tz}` : ''}
          </div>
          <div className="rule-detail" style={{ marginTop: 6 }}>
            <span className="chip" title="Model output, not a weather-station reading">Modeled</span>{' '}
            Weather and heat values:{' '}
            <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">
              Open-Meteo
            </a>{' '}
            forecast model (current values are a model nowcast, not a station reading). Air quality: CAMS model
            {zip.sources?.airQuality?.status === 'unavailable' ? ' — unavailable for this request' : ''}. Forecast days are forecasts.
          </div>
        </div>
      </div>

      {zip.forecast?.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <h3>8-day forecast</h3>
          <div className="forecast-row">
            {zip.forecast.map((day, i) => (
              <ForecastDay key={day.date} day={day} isTomorrow={i === 0} zip={zip} dryBulbTiers={dryBulbTiers} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
